import {
  STORE_ASSETS,
  STORE_PARTIALS,
  toPromise,
  withStore,
} from '../storage/idb.js';
import type { AssetStore, CachedAsset, CachedAssetMeta } from './store.js';

interface AssetRow {
  readonly sha256: string;
  readonly bytes: ArrayBuffer;
  readonly byte_length: number;
  readonly media_type: string;
  readonly stored_at_ms: number;
  last_access_ms: number;
}

interface PartialRow {
  readonly sha256: string;
  readonly bytes: ArrayBuffer;
}

/** The WebView implementation of the asset store. Bytes are held as ArrayBuffers. */
export function createIdbAssetStore(db: IDBDatabase): AssetStore {
  return {
    has: async (sha256) => {
      const row = await withStore(db, STORE_ASSETS, 'readonly', (store) =>
        toPromise<AssetRow | undefined>(store.get(sha256)),
      );
      return row !== undefined;
    },

    get: async (sha256) => {
      const row = await withStore(db, STORE_ASSETS, 'readonly', (store) =>
        toPromise<AssetRow | undefined>(store.get(sha256)),
      );
      if (row === undefined) return null;
      const asset: CachedAsset = {
        sha256: row.sha256,
        bytes: new Uint8Array(row.bytes),
        byte_length: row.byte_length,
        media_type: row.media_type,
        stored_at_ms: row.stored_at_ms,
        last_access_ms: row.last_access_ms,
      };
      return asset;
    },

    put: async ({ sha256, bytes, media_type, at_ms }) => {
      const row: AssetRow = {
        sha256,
        bytes: copyToArrayBuffer(bytes),
        byte_length: bytes.byteLength,
        media_type,
        stored_at_ms: at_ms,
        last_access_ms: at_ms,
      };
      await withStore(db, STORE_ASSETS, 'readwrite', (store) => {
        store.put(row);
      });
    },

    delete: async (sha256) => {
      await withStore(db, STORE_ASSETS, 'readwrite', (store) => {
        store.delete(sha256);
      });
    },

    list: async () => {
      const rows = await withStore(db, STORE_ASSETS, 'readonly', (store) =>
        toPromise<AssetRow[]>(store.getAll()),
      );
      return rows.map<CachedAssetMeta>((row) => ({
        sha256: row.sha256,
        byte_length: row.byte_length,
        media_type: row.media_type,
        stored_at_ms: row.stored_at_ms,
        last_access_ms: row.last_access_ms,
      }));
    },

    touch: async (sha256, atMs) => {
      await withStore(db, STORE_ASSETS, 'readwrite', async (store) => {
        const row = await toPromise<AssetRow | undefined>(store.get(sha256));
        if (row !== undefined) store.put({ ...row, last_access_ms: atMs });
      });
    },

    totalBytes: async () => {
      const rows = await withStore(db, STORE_ASSETS, 'readonly', (store) =>
        toPromise<AssetRow[]>(store.getAll()),
      );
      return rows.reduce((total, row) => total + row.byte_length, 0);
    },

    readPartial: async (sha256) => {
      const row = await withStore(db, STORE_PARTIALS, 'readonly', (store) =>
        toPromise<PartialRow | undefined>(store.get(sha256)),
      );
      return row === undefined ? null : new Uint8Array(row.bytes);
    },

    writePartial: async (sha256, bytes) => {
      await withStore(db, STORE_PARTIALS, 'readwrite', (store) => {
        store.put({ sha256, bytes: copyToArrayBuffer(bytes) } satisfies PartialRow);
      });
    },

    clearPartial: async (sha256) => {
      await withStore(db, STORE_PARTIALS, 'readwrite', (store) => {
        store.delete(sha256);
      });
    },
  };
}

function copyToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}
